import classNames from "classnames";
import uniq from "lodash/uniq";
import React, { FunctionComponent, ReactNode, useRef, useState } from "react";
import { useIntl } from "react-intl";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { ReactComponent as IconArrowLeft } from "../../../../icons/chevron-left.svg";
import { ReactComponent as IconArrowRight } from "../../../../icons/chevron-right.svg";
import { TemplatePageDefinition } from "../../../../types/customization/template";
import { isDesktopSize } from "../../../../utils/customization/device";
import {
  findPageDefinition,
  findPageDefinitionsWithValidCondition,
} from "../../../../utils/customization/finders";
import { findLocalizedContent } from "../../../../utils/customization/localized";
import { showCellColorOverPageColor } from "../../../../utils/customization/template";
import Page from "../Page";
import "./style.scss";

const MAX_LAYOUT_SIZE = 112;

interface Props {
  currentPageDefinitionKey?: string;
  onColorChange?: (colorKey: string) => void;
  pageKey: string;
  undoKey?: string;
}

const CoverLayouts: FunctionComponent<Props> = ({
  currentPageDefinitionKey,
  onColorChange,
  pageKey,
  undoKey,
}) => {
  const { configuration, content, dispatch, template } = useCustomizationContext();
  const intl = useIntl();

  const [colorByPages, setColorByPages] = useState<{ [pageKey: string]: string }>({});

  const filteredPageDefinitions = findPageDefinitionsWithValidCondition(
    template,
    content,
    {
      type: "front-cover",
    },
    configuration?.options
  );

  let selectedPageKey = "";
  if (currentPageDefinitionKey) {
    selectedPageKey = currentPageDefinitionKey;
  } else {
    const pageDefinitionKeys = uniq(
      content.pages.map(({ pageDefinitionKey }) => pageDefinitionKey)
    );
    if (pageDefinitionKeys.length === 1) {
      selectedPageKey = pageDefinitionKeys[0];
    }
  }

  const handleClickOnLayout = (pageDefinition: TemplatePageDefinition) => {
    dispatch({
      options: configuration?.options,
      pageDefinitionKey: pageDefinition.key,
      pageKey,
      type: "changePagePageDefinition",
      undoKey,
    });
    if (colorByPages[pageDefinition.key] && !!onColorChange) {
      onColorChange(colorByPages[pageDefinition.key]);
    }
  };

  let currentPageDefinition: TemplatePageDefinition | undefined;
  if (currentPageDefinitionKey) {
    currentPageDefinition = findPageDefinition(currentPageDefinitionKey, template);
  }

  const registerColor = (pageDefinitionKey: string, colorDefinitionKey: string) => {
    setColorByPages((colors) => ({ ...colors, [pageDefinitionKey]: colorDefinitionKey }));
  };

  const handleSelectColor = (pageDefinitionKey: string, colorDefinitionKey: string) => {
    registerColor(pageDefinitionKey, colorDefinitionKey);

    if (currentPageDefinition?.key === pageDefinitionKey && !!onColorChange) {
      onColorChange(colorDefinitionKey);

      const thumbnail = document.getElementById(`thumbnail-${pageDefinitionKey}`);
      if (thumbnail && !isDesktopSize()) {
        thumbnail.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center",
        });
      }
    }
  };

  const tmpCoverLayouts = filteredPageDefinitions.map((pageDefinition, index) => {
    if (
      currentPageDefinition &&
      (currentPageDefinition.type !== pageDefinition.type ||
        currentPageDefinition.filterType !== pageDefinition.filterType)
    ) {
      return null;
    }

    const localizedTitle =
      pageDefinition.title && findLocalizedContent(pageDefinition.title, content.locale);
    const coverTitle = localizedTitle
      ? localizedTitle.content
      : `${intl.formatMessage({ id: "kustom_CoverPanel_cover" })} ${index + 1}`;

    const ppmmHeight = MAX_LAYOUT_SIZE / pageDefinition.height.value;
    const ppmmWidth = MAX_LAYOUT_SIZE / pageDefinition.width.value;
    const ppmm = Math.min(ppmmHeight, ppmmWidth);

    const currentPageColors = template.colorDefinitions.filter((color) =>
      pageDefinition.backgroundColorKeys.includes(color.key)
    );

    if (!colorByPages[pageDefinition.key]) {
      registerColor(pageDefinition.key, pageDefinition.backgroundColorKeys[0]);
    }
    const colorDisplayed = colorByPages[pageDefinition.key]
      ? colorByPages[pageDefinition.key]
      : pageDefinition.backgroundColorKeys[0];

    const showCellColor = showCellColorOverPageColor(template.templateType, currentPageColors);

    const colorItems = currentPageColors.map((pageColor) => {
      const backgroundColor =
        showCellColor && pageColor.cellColors
          ? pageColor.cellColors[0].active.backgroundValue
          : pageColor.backgroundValue;
      return (
        <div
          className={classNames("CoverLayouts-item-colors-chip", {
            "CoverLayouts-item-colors-chip--selected":
              pageColor.key === colorDisplayed && currentPageColors.length > 1,
          })}
          key={pageColor.key}
          onClick={() => handleSelectColor(pageDefinition.key, pageColor.key)}
          style={{ backgroundColor: backgroundColor }}
        />
      );
    });

    return (
      <div
        className={classNames("CoverLayouts-item", {
          "CoverLayouts-item--selected": pageDefinition.key === selectedPageKey,
        })}
        id={`thumbnail-${pageDefinition.key}`}
        key={pageDefinition.key}
      >
        <div
          className="CoverLayouts-item-preview"
          onClick={() => handleClickOnLayout(pageDefinition)}
        >
          <Page
            content={content}
            forcedColorKey={colorDisplayed}
            forcedPageDefinition={pageDefinition}
            pageKey={pageKey || ""}
            ppmm={ppmm}
            skipEvents={true}
            template={template}
          />
        </div>

        <div
          className="CoverLayouts-item-title"
          onClick={() => handleClickOnLayout(pageDefinition)}
        >
          {coverTitle}
        </div>

        <div
          className={classNames("CoverLayouts-item-colors", {
            "CoverLayouts-item-colors--unique": currentPageColors.length === 1,
          })}
        >
          {currentPageColors.length > 5 ? (
            <ColorsSlider>{colorItems}</ColorsSlider>
          ) : (
            <>{colorItems}</>
          )}
        </div>
      </div>
    );
  });

  return <div className="CoverLayouts">{tmpCoverLayouts}</div>;
};

const ColorsSlider = ({ children }: { children: ReactNode }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [prevHidden, setPrevHidden] = useState<boolean>(true);
  const [nextHidden, setNextHidden] = useState<boolean>(false);

  const handleClickPagination = (step: number) => {
    if (wrapperRef && wrapperRef.current) {
      const element = wrapperRef.current;
      const scrollAmount = Math.min(
        element.scrollWidth - element.offsetWidth,
        Math.max(0, element.scrollLeft + step * element.offsetWidth)
      );
      element.scrollLeft = scrollAmount;

      setPrevHidden(scrollAmount === 0);
      setNextHidden(scrollAmount === element.scrollWidth - element.offsetWidth);
    }
  };

  return (
    <div className="CoverLayouts-item-colors-slider">
      <div
        className={classNames("CoverLayouts-item-colors-slider-button", {
          "CoverLayouts-item-colors-slider-button--disabled": prevHidden,
        })}
        onClick={() => handleClickPagination(-1)}
      >
        <IconArrowLeft />
      </div>
      <div className="CoverLayouts-item-colors-slider-wrapper" ref={wrapperRef}>
        <div className="CoverLayouts-item-colors-slider-content">{children}</div>
      </div>
      <div
        className={classNames("CoverLayouts-item-colors-slider-button", {
          "CoverLayouts-item-colors-slider-button--disabled": nextHidden,
        })}
        onClick={() => handleClickPagination(1)}
      >
        <IconArrowRight />
      </div>
    </div>
  );
};

export default CoverLayouts;
